import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlaneIcon, BuildingIcon, PackageIcon, CalendarIcon, UsersIcon, SearchIcon, ArrowRightIcon, PlusIcon } from 'lucide-react';
import Button from '../common/Button';
type SearchType = 'flight' | 'hotel' | 'package';
type FlightType = 'oneWay' | 'roundTrip' | 'multiCity';
const SearchWidget: React.FC = () => {
  const navigate = useNavigate();
  const [searchType, setSearchType] = useState<SearchType>('flight');
  const [flightType, setFlightType] = useState<FlightType>('roundTrip');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [departureDate, setDepartureDate] = useState('');
  const [returnDate, setReturnDate] = useState('');
  const [hotelLocation, setHotelLocation] = useState('');
  const [checkInDate, setCheckInDate] = useState('');
  const [checkOutDate, setCheckOutDate] = useState('');
  const [travelers, setTravelers] = useState('1 Adult');
  const [multiCityFlights, setMultiCityFlights] = useState([{
    from: '',
    to: '',
    date: ''
  }, {
    from: '',
    to: '',
    date: ''
  }]);
  const handleAddFlight = () => {
    setMultiCityFlights([...multiCityFlights, {
      from: '',
      to: '',
      date: ''
    }]);
  };
  const handleMultiCityChange = (index: number, field: 'from' | 'to' | 'date', value: string) => {
    const updatedFlights = [...multiCityFlights];
    updatedFlights[index] = {
      ...updatedFlights[index],
      [field]: value
    };
    setMultiCityFlights(updatedFlights);
  };
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    let searchParams = new URLSearchParams();
    searchParams.append('type', searchType);
    if (searchType === 'flight') {
      searchParams.append('flightType', flightType);
      if (flightType === 'multiCity') {
        searchParams.append('multiCity', JSON.stringify(multiCityFlights));
      } else {
        searchParams.append('from', from);
        searchParams.append('to', to);
        searchParams.append('departureDate', departureDate);
        if (flightType === 'roundTrip') {
          searchParams.append('returnDate', returnDate);
        }
      }
    } else if (searchType === 'hotel') {
      searchParams.append('location', hotelLocation);
      searchParams.append('checkIn', checkInDate);
      searchParams.append('checkOut', checkOutDate);
    } else if (searchType === 'package') {
      // For packages, include both flight and hotel parameters
      searchParams.append('from', from);
      searchParams.append('to', to);
      searchParams.append('departureDate', departureDate);
      searchParams.append('returnDate', returnDate);
      searchParams.append('hotelLocation', hotelLocation || to); // Use destination as hotel location if not specified
      searchParams.append('checkIn', checkInDate || departureDate); // Use departure date as check-in if not specified
      searchParams.append('checkOut', checkOutDate || returnDate); // Use return date as check-out if not specified
    }
    searchParams.append('travelers', travelers);
    navigate(`/search?${searchParams.toString()}`);
  };
  return <div className="bg-white rounded-lg shadow-lg p-6" data-id="element-48">
      {/* Search type selector */}
      <div className="flex space-x-4 mb-6" data-id="element-49">
        <button onClick={() => setSearchType('flight')} className={`flex items-center px-4 py-2 rounded-full text-sm font-medium ${searchType === 'flight' ? 'bg-blue-100 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-50">
          <PlaneIcon className="h-4 w-4 mr-2" data-id="element-51" />
          Flights
        </button>
        <button onClick={() => setSearchType('hotel')} className={`flex items-center px-4 py-2 rounded-full text-sm font-medium ${searchType === 'hotel' ? 'bg-blue-100 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-52">
          <BuildingIcon className="h-4 w-4 mr-2" data-id="element-53" />
          Hotels
        </button>
        <button onClick={() => setSearchType('package')} className={`flex items-center px-4 py-2 rounded-full text-sm font-medium ${searchType === 'package' ? 'bg-blue-100 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-54">
          <PackageIcon className="h-4 w-4 mr-2" data-id="element-55" />
          Flight + Hotel
        </button>
      </div>
      {/* Flight type selector - only show for flights */}
      {searchType === 'flight' && <div className="flex space-x-4 mb-6" data-id="element-56">
          <button onClick={() => setFlightType('roundTrip')} className={`px-3 py-1 rounded-md text-sm font-medium ${flightType === 'roundTrip' ? 'bg-blue-50 text-blue-600 border border-blue-200' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-57">
            Round Trip
          </button>
          <button onClick={() => setFlightType('oneWay')} className={`px-3 py-1 rounded-md text-sm font-medium ${flightType === 'oneWay' ? 'bg-blue-50 text-blue-600 border border-blue-200' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-58">
            One Way
          </button>
          <button onClick={() => setFlightType('multiCity')} className={`px-3 py-1 rounded-md text-sm font-medium ${flightType === 'multiCity' ? 'bg-blue-50 text-blue-600 border border-blue-200' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-59">
            Multi-City
          </button>
        </div>}
      {/* Search form */}
      <form onSubmit={handleSearch} className="space-y-4" data-id="element-60">
        {/* Flight search fields */}
        {(searchType === 'flight' || searchType === 'package') && flightType !== 'multiCity' && <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-61">
              {/* From field */}
              <div className="relative" data-id="element-62">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-63">
                  From
                </label>
                <input type="text" value={from} onChange={e => setFrom(e.target.value)} placeholder="City or airport" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-64" />
                <div className="absolute left-3 top-8 text-gray-400" data-id="element-65">
                  <PlaneIcon className="h-5 w-5 transform -rotate-45" data-id="element-66" />
                </div>
              </div>
              {/* To field */}
              <div className="relative" data-id="element-67">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-68">
                  To
                </label>
                <input type="text" value={to} onChange={e => setTo(e.target.value)} placeholder="City or airport" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-69" />
                <div className="absolute left-3 top-8 text-gray-400" data-id="element-70">
                  <PlaneIcon className="h-5 w-5 transform -rotate-45" data-id="element-71" />
                </div>
              </div>
              {/* Departure date */}
              <div className="relative" data-id="element-72">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-73">
                  Departure
                </label>
                <input type="date" value={departureDate} onChange={e => setDepartureDate(e.target.value)} className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-74" />
                <div className="absolute left-3 top-8 text-gray-400" data-id="element-75">
                  <CalendarIcon className="h-5 w-5" data-id="element-76" />
                </div>
              </div>
              {/* Return date - only for round trip */}
              {(flightType === 'roundTrip' || searchType === 'package') && <div className="relative" data-id="element-77">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-78">
                    Return
                  </label>
                  <input type="date" value={returnDate} onChange={e => setReturnDate(e.target.value)} className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-79" />
                  <div className="absolute left-3 top-8 text-gray-400" data-id="element-80">
                    <CalendarIcon className="h-5 w-5" data-id="element-81" />
                  </div>
                </div>}
              {/* Travelers field */}
              {searchType === 'flight' && flightType !== 'multiCity' && <div className="relative" data-id="element-82">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-83">
                    Travelers
                  </label>
                  <input type="text" value={travelers} onChange={e => setTravelers(e.target.value)} placeholder="Add travelers" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-84" />
                  <div className="absolute left-3 top-8 text-gray-400" data-id="element-85">
                    <UsersIcon className="h-5 w-5" data-id="element-86" />
                  </div>
                </div>}
            </div>}
        {/* Multi-city flight search fields */}
        {searchType === 'flight' && flightType === 'multiCity' && <div className="space-y-4" data-id="element-87">
            {multiCityFlights.map((flight, index) => <div key={index} className="grid grid-cols-1 md:grid-cols-3 gap-4 p-3 border border-gray-200 rounded-md" data-id="element-88">
                <div className="relative" data-id="element-89">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-90">
                    From
                  </label>
                  <input type="text" value={flight.from} onChange={e => handleMultiCityChange(index, 'from', e.target.value)} placeholder="City or airport" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-91" />
                  <div className="absolute left-3 top-8 text-gray-400" data-id="element-92">
                    <PlaneIcon className="h-5 w-5 transform -rotate-45" data-id="element-93" />
                  </div>
                </div>
                <div className="relative" data-id="element-94">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-95">
                    To
                  </label>
                  <input type="text" value={flight.to} onChange={e => handleMultiCityChange(index, 'to', e.target.value)} placeholder="City or airport" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-96" />
                  <div className="absolute left-3 top-8 text-gray-400" data-id="element-97">
                    <PlaneIcon className="h-5 w-5 transform -rotate-45" data-id="element-98" />
                  </div>
                </div>
                <div className="relative" data-id="element-99">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-100">
                    Date
                  </label>
                  <input type="date" value={flight.date} onChange={e => handleMultiCityChange(index, 'date', e.target.value)} className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-101" />
                  <div className="absolute left-3 top-8 text-gray-400" data-id="element-102">
                    <CalendarIcon className="h-5 w-5" data-id="element-103" />
                  </div>
                </div>
              </div>)}
            <div className="flex justify-between items-center" data-id="element-104">
              <button type="button" onClick={handleAddFlight} className="flex items-center text-sm text-blue-600 hover:text-blue-800" data-id="element-105">
                <PlusIcon className="h-4 w-4 mr-1" data-id="element-106" />
                Add another flight
              </button>
              <div className="relative" data-id="element-107">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-108">
                  Travelers
                </label>
                <input type="text" value={travelers} onChange={e => setTravelers(e.target.value)} placeholder="Add travelers" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-109" />
                <div className="absolute left-3 top-8 text-gray-400" data-id="element-110">
                  <UsersIcon className="h-5 w-5" data-id="element-111" />
                </div>
              </div>
            </div>
          </div>}
        {/* Hotel search fields */}
        {searchType === 'hotel' && <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-112">
            {/* Destination field */}
            <div className="relative" data-id="element-113">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-114">
                Destination
              </label>
              <input type="text" value={hotelLocation} onChange={e => setHotelLocation(e.target.value)} placeholder="City or specific hotel" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-115" />
              <div className="absolute left-3 top-8 text-gray-400" data-id="element-116">
                <BuildingIcon className="h-5 w-5" data-id="element-117" />
              </div>
            </div>
            {/* Check-in date */}
            <div className="relative" data-id="element-118">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-119">
                Check-in
              </label>
              <input type="date" value={checkInDate} onChange={e => setCheckInDate(e.target.value)} className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-120" />
              <div className="absolute left-3 top-8 text-gray-400" data-id="element-121">
                <CalendarIcon className="h-5 w-5" data-id="element-122" />
              </div>
            </div>
            {/* Check-out date */}
            <div className="relative" data-id="element-123">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-124">
                Check-out
              </label>
              <input type="date" value={checkOutDate} onChange={e => setCheckOutDate(e.target.value)} className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-125" />
              <div className="absolute left-3 top-8 text-gray-400" data-id="element-126">
                <CalendarIcon className="h-5 w-5" data-id="element-127" />
              </div>
            </div>
            {/* Travelers field */}
            <div className="relative" data-id="element-128">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-129">
                Guests
              </label>
              <input type="text" value={travelers} onChange={e => setTravelers(e.target.value)} placeholder="Add guests" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-130" />
              <div className="absolute left-3 top-8 text-gray-400" data-id="element-131">
                <UsersIcon className="h-5 w-5" data-id="element-132" />
              </div>
            </div>
          </div>}
        {/* Package search fields (both flight and hotel) */}
        {searchType === 'package' && <div className="space-y-6" data-id="element-133">
            <div className="border-t border-gray-200 pt-4 mt-4" data-id="element-134">
              <h3 className="text-sm font-medium text-gray-700 mb-3" data-id="element-135">
                Hotel Details
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-136">
                {/* Hotel location - defaults to flight destination if not specified */}
                <div className="relative" data-id="element-137">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-138">
                    Hotel Location
                  </label>
                  <input type="text" value={hotelLocation || to} onChange={e => setHotelLocation(e.target.value)} placeholder="City or specific hotel" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-139" />
                  <div className="absolute left-3 top-8 text-gray-400" data-id="element-140">
                    <BuildingIcon className="h-5 w-5" data-id="element-141" />
                  </div>
                </div>
                {/* Check-in date - defaults to flight departure date if not specified */}
                <div className="relative" data-id="element-142">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-143">
                    Check-in
                  </label>
                  <input type="date" value={checkInDate || departureDate} onChange={e => setCheckInDate(e.target.value)} className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-144" />
                  <div className="absolute left-3 top-8 text-gray-400" data-id="element-145">
                    <CalendarIcon className="h-5 w-5" data-id="element-146" />
                  </div>
                </div>
                {/* Check-out date - defaults to flight return date if not specified */}
                <div className="relative" data-id="element-147">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-148">
                    Check-out
                  </label>
                  <input type="date" value={checkOutDate || returnDate} onChange={e => setCheckOutDate(e.target.value)} className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-149" />
                  <div className="absolute left-3 top-8 text-gray-400" data-id="element-150">
                    <CalendarIcon className="h-5 w-5" data-id="element-151" />
                  </div>
                </div>
              </div>
            </div>
            {/* Travelers field for packages */}
            <div className="relative" data-id="element-152">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-153">
                Travelers
              </label>
              <input type="text" value={travelers} onChange={e => setTravelers(e.target.value)} placeholder="Add travelers" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-154" />
              <div className="absolute left-3 top-8 text-gray-400" data-id="element-155">
                <UsersIcon className="h-5 w-5" data-id="element-156" />
              </div>
            </div>
          </div>}
        {/* Search button */}
        <div className="flex justify-end" data-id="element-157">
          <Button type="submit" variant="primary" className="flex items-center px-6" data-id="element-158">
            <SearchIcon className="h-4 w-4 mr-2" data-id="element-159" />
            Search {searchType === 'package' ? 'Packages' : `${searchType}s`}
            <ArrowRightIcon className="h-4 w-4 ml-2" data-id="element-160" />
          </Button>
        </div>
      </form>
      {/* Quick links */}
      {searchType === 'package' && <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-161">
          <p className="text-sm text-gray-600 mb-2" data-id="element-162">Popular packages:</p>
          <div className="flex flex-wrap gap-2" data-id="element-163">
            <button onClick={() => {
          setFrom('New York');
          setTo('Paris');
          setHotelLocation('Paris');
        }} className="text-sm text-blue-600 hover:text-blue-800 hover:underline" data-id="element-164">
              New York to Paris
            </button>
            <span className="text-gray-300" data-id="element-165">•</span>
            <button onClick={() => {
          setFrom('London');
          setTo('Rome');
          setHotelLocation('Rome');
        }} className="text-sm text-blue-600 hover:text-blue-800 hover:underline" data-id="element-166">
              London to Rome
            </button>
            <span className="text-gray-300" data-id="element-167">•</span>
            <button onClick={() => {
          setFrom('Los Angeles');
          setTo('Tokyo');
          setHotelLocation('Tokyo');
        }} className="text-sm text-blue-600 hover:text-blue-800 hover:underline" data-id="element-168">
              LA to Tokyo
            </button>
          </div>
        </div>}
    </div>;
};
export default SearchWidget;